import { nonnull } from "@tento-core/assert";
import { Json, type Option, type Timescale } from "@tento-lona/sheets";
import {
  isCalendarCellData,
  NaiveDate,
  PartialDate,
  Weekday,
} from "@tento-chrono";
import { LonaIcons, NumberIcon } from "@tento-ui/ui/icons";
import { ColumnIndex } from "@tento-ui/components/sheet/column-index";
import {
  type BindCellContext,
  type RenderPlugin,
  type StaticChildSpec,
} from "@tento-lona/sheets-ui/sheet/render-plugin.ts";
import { type SheetRow } from "@tento-lona";
import { SheetDelegate } from "@tento-lona/sheets-ui/sheet/sheet-delegate.ts";
import { ColumnHeader, reuseHeaderChild } from "../shared/column-header/cell.ts";
import { $$ } from "@tento-ui/fastdom";
import { ColumnResolver, SpanRangeCalculator } from "../shared/column-header/span-range.ts";
import { GESTURE_MANAGER } from "@tento-ui/gesture-manager";
import { SlotRow } from "@tento-ui/components/slot/slot-row";
import { LiveData } from "@tento-core/livedata";
import { DateFormat, type FirstDay } from "@tento-lona";
import { Column } from "@tento-ui/component-builtin";
import { DomUtils } from "@tento-ui/dom";
import { getSubHeaderFormatter } from "../shared/column-header/sub-header-formatters.ts";
import { shortWeekdayLabels } from "../shared/column-header/month-weekday-header.ts";
import { COLUMN_HEADER_SECONDARY_SPEC } from "@tento-lona/sheets/platform";
import type { PerTypeSpec } from "@tento-lona/sheets/platform";

const MONTH_WEEKDAY_DIVIDER_BACKGROUND = `repeating-linear-gradient(
  to right,
  transparent,
  transparent calc(100% / 7 - 1px),
  var(--divider) calc(100% / 7 - 1px),
  var(--divider) calc(100% / 7)
)`;

/**
 * The renderer plugin for the second header row: a secondary calendar
 * system's dates, or the month and weekday.
 */
export class ColumnHeaderSecondaryRendererPlugin
  implements RenderPlugin.Renderer<HTMLElement, HTMLElement>
{
  static readonly spec: PerTypeSpec = COLUMN_HEADER_SECONDARY_SPEC;

  readonly role = "renderer";
  readonly id = "column.header-secondary";
  readonly icon: HTMLTemplateElement = LonaIcons.$template(NumberIcon);
  #monthWeekdayHeaders = new Map<string, HTMLElement>();

  #dateFormat: LiveData.Readable<DateFormat>;
  #onSelectHeader: Option<(d: NaiveDate.Partial.WithTz) => void>;
  #firstDay: LiveData.Readable<FirstDay>;
  constructor(
    firstDay: LiveData.Readable<FirstDay> = new LiveData<FirstDay>(
      "monday",
    ),
    onSelectHeader: Option<(d: NaiveDate.Partial.WithTz) => void>,
    dateFormat: LiveData.Readable<DateFormat> = new LiveData<DateFormat>(
      "month-day",
    ),
  ) {
    this.#firstDay = firstDay;
    this.#onSelectHeader = onSelectHeader;
    this.#dateFormat = dateFormat;
  }

  makeCell(): HTMLElement {
    return Column.make();
  }

  onResize($cell: HTMLElement, context: BindCellContext): void {
    this.bindCell($cell, context);
  }

  async bindCell(
    $cell: HTMLElement,
    { column, widthPx, sheet, handle, row, now: columnNow }: BindCellContext,
  ) {
    const attrs = row.attributes();
    const calendarSystem = Json.string(attrs, "calendarSystem") ?? "gregorian";
    if (calendarSystem === "month-weekday") {
      $$.mutate(() => {
        DomUtils.clearChildren($cell);
        $cell.assignStyles({ paddingInline: "0px" });
      });
      return;
    }

    const fmt = getSubHeaderFormatter(calendarSystem);
    const isXs = (Json.string(attrs, "size") ?? "normal") === "xs";
    const isGregorian = calendarSystem === "gregorian";
    const ir = sheet.indexRenderer;
    $$.mutate(() => {
      // Children are reused in place per kind (TimelineLegend for year/month,
      // SlotRow for day/week): reconnecting a header child rebuilds its
      // shadow-scope style resolver, and these cells rebind per column
      // crossing during scroll.
      const c = column.discrete();
      const tz = c[1];
      const onSelectHeader = this.#onSelectHeader;

      const now = columnNow;

      const mapper = ir.mapper;
      const weekStart = this.#startOfWeek();
      const resolver: ColumnResolver = {
        resolveDate(date: NaiveDate, granularity: Timescale) {
          const pd = PartialDate.fromDateAndType(granularity, date, weekStart);
          const key = ColumnIndex.Key.fromPartial(pd, "lower");
          const resolved = mapper.resolveKeyToDefault(key, "lower");
          return {
            colIdx: resolved.offset,
            colRange: resolved.pd.toRange(),
          };
        },
        getColumnWidthPx(colIdx: number) {
          return handle.state.getColumnWidthPx(colIdx);
        },
      };

      switch (c[0].type) {
        case "year": {
          $cell.assignStyles({ paddingInline: "0px" });
          const $tl = reuseHeaderChild($cell, ColumnHeader.TimelineLegend);
          $tl.bind(
            c[0].toRange().start,
            fmt.yearSpans(c[0], widthPx),
            SpanRangeCalculator.make(c[0].type, column.offset, resolver),
            now,
            fmt.yearSpanFormatter(widthPx),
            onSelectHeader
              ? (span) => {
                  const partial = new NaiveDate.Partial(
                    "month",
                    span.start.yr,
                    span.start.month1,
                  );
                  onSelectHeader([partial, tz]);
                }
              : null,
          );
          for (const $card of $tl.children)
            ($card as HTMLElement).toggleAttribute("xs", isXs);
          break;
        }
        case "month": {
          $cell.assignStyles({ paddingInline: "0px" });
          const $tl = reuseHeaderChild($cell, ColumnHeader.TimelineLegend);
          const weekStarts = ColumnHeader.getWeekStartsInMonth(c[0], weekStart);
          const weekSpans = weekStarts.map((start) => {
            const weekEnd = start.addDays(7);
            return new NaiveDate.Range(start, weekEnd);
          });
          $tl.bind(
            c[0].toRange().start,
            weekSpans,
            SpanRangeCalculator.make(c[0].type, column.offset, resolver),
            now,
            fmt.monthSpanFormatter(),
            onSelectHeader
              ? (span) => {
                  const partial = new NaiveDate.Partial(
                    "week",
                    span.start.yr,
                    span.start.isoWno,
                  );
                  onSelectHeader([partial, tz]);
                }
              : null,
          );
          for (const $card of $tl.children)
            ($card as HTMLElement).toggleAttribute("xs", isXs);
          break;
        }
        case "day":
        case "week": {
          if (c[0].type === "day") {
            $cell.assignStyles({ paddingInline: "1px" });
          } else {
            $cell.assignStyles({ paddingInline: "2px" });
          }
          const dates = [...c[0].toRange().iter()];
          const today = tz.date(now).date.start.date;
          const $row = reuseHeaderChild($cell, SlotRow);
          $row.bind(dates.length, (i, $card) => {
            const date = nonnull(dates[i], "a header card per date");
            $card.toggleAttribute("small", true);
            $card.toggleAttribute("xs", isXs);

            let label: string;
            if (!isGregorian) {
              const cellEntry = row.single()?.peek(date) ?? null;

              const cellValue = cellEntry?.value;
              label =
                cellValue != null && isCalendarCellData(cellValue)
                  ? cellValue.label
                  : fmt.weekDayLabel(date);
            } else {
              label = fmt.weekDayLabel(date);
            }

            const sublabel = isXs
              ? ""
              : widthPx < 54
                ? date.dayOfWeek.shortname.slice(0, 1)
                : date.dayOfWeek.shortname;
            $card.bind(label, sublabel, today.dse === date.dse);
            if (!onSelectHeader || isXs) return;
            GESTURE_MANAGER.addPointerEvent($card, {
              onClick: () => {
                $card.flash();
                const partial = new NaiveDate.Partial(
                  "day",
                  date.yr,
                  date.month1,
                  date.day,
                );
                onSelectHeader([partial, tz]);
              },
            });
          });
          break;
        }
      }
    });
  }

  #startOfWeek(): Weekday {
    return this.#firstDay.get() === "monday" ? Weekday.MON : Weekday.SUN;
  }

  #$monthWeekdayHeaderForRow(row: SheetRow.Joined.Handle): HTMLElement {
    const key = row.id.toString();
    let $header = this.#monthWeekdayHeaders.get(key);
    if (!$header) {
      $header = document.createElement("div");
      $header.setAttribute("role", "row");
      $header.style.alignItems = "stretch";
      $header.style.backgroundColor = "var(--header-bg, var(--bg-elevated))";
      $header.style.backgroundImage = MONTH_WEEKDAY_DIVIDER_BACKGROUND;
      $header.style.boxSizing = "border-box";
      $header.style.display = "grid";
      $header.style.gridTemplateColumns = "repeat(7, minmax(0, 1fr))";
      $header.style.height = "38px";
      $header.style.padding = "2px 0px";
      $header.style.pointerEvents = "none";
      this.#monthWeekdayHeaders.set(key, $header);
    }
    this.#bindMonthWeekdayHeader($header);
    return $header;
  }

  #bindMonthWeekdayHeader($header: HTMLElement): void {
    DomUtils.clearChildren($header);
    for (const label of shortWeekdayLabels(this.#startOfWeek())) {
      const $label = document.createElement("p");
      $label.setAttribute("role", "columnheader");
      $label.textContent = label;
      $label.style.alignItems = "center";
      $label.style.color = "var(--text-ter)";
      $label.style.display = "flex";
      $label.style.justifyContent = "center";
      $label.style.minWidth = "0px";
      $label.style.setProperty("--font-size", "var(--text-8px)");
      $label.style.setProperty("--margin-bottom", "0px");
      $label.style.setProperty("--margin-top", "0px");
      $label.style.whiteSpace = "nowrap";
      $header.appendChild($label);
    }
  }

  staticChildren(
    row: SheetRow.Joined.Handle,
    _handle: SheetDelegate,
  ): StaticChildSpec[] {
    if (Json.string(row.attributes(), "calendarSystem") !== "month-weekday") return [];
    return [
      {
        id: "month-weekday-header",
        $element: this.#$monthWeekdayHeaderForRow(row),
        fillWidth: true,
      },
    ];
  }

  optionsInfo(_row: SheetRow.Joined.Handle): Option<string> {
    return null;
  }

  makeRowOptions = null;
  loadData = null;
  legendFactory = null;
  onBindRow(_sheet: SheetDelegate, _row: SheetRow.Joined.Handle): void {}
  onViewportColumnsChanged(
    _row: SheetRow.Joined.Handle,
    _columns: ColumnIndex.Key.Resolved.Default.Range,
    _handle: SheetDelegate,
    _$legend: Option<HTMLElement>,
  ): void {}
  onRenderedColumnsWillChange(): void {}
}
